import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnChanges, SimpleChanges, ViewRef, inject } from '@angular/core';
import { Router } from '@angular/router';
import { IModel, IModelData, IModelDataV2WithUrl, Model } from '@df/catalog/model';
import { IProductDataV1, Product } from '@df/catalog/product/product';
import { App } from '@df/core/app';
import { Status } from '@df/core/status';
import { TimeoutService } from '@df/ng/timeout.service';
import { NDfModal } from '@df/ui/modal/df-modal.interface';
import { ModalRef } from '@df/ui/modal/modal-ref';
import { ICmsModalService } from '@icms/client/assets/modal/icms-modal.service';
import { NICmsContentComponent } from '@icms/core/content';
import { ICmsNotificationService } from '@icms/ui/notification/icms-notification.service';
import isEmpty from 'lodash-es/isEmpty';
import map from 'lodash-es/map';
import { ICmsShortcutsService } from '../icms-shortcuts.service';

type TItem = Model<IModelDataV2WithUrl> | NDfModal.IConfig;
type TResult = { title: string; items: TItem[] };

@Component({
    standalone: false,
    selector: 'icms-shortcuts-search',
    templateUrl: './icms-shortcuts-search.html',
    styleUrls: ['./icms-shortcuts-search.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ICmsShortcutsSearchComponent implements OnChanges {
    /**
     * Search query entered by user
     */
    @Input({
        required: true
    })
    query!: string;

    /**
     * Search result
     */
    protected results?: TResult[];

    protected modals;

    /**
     * Search status
     */
    protected status = new Status();

    /**
     * Selected model
     */
    protected selected?: TItem;

    /**
     * Services
     */
    private timeoutService = inject(TimeoutService);
    private app = inject(App);
    private changeDetectorRef = inject(ChangeDetectorRef);
    private router = inject(Router);
    private iCmsModalService = inject(ICmsModalService);
    private iCmsNotificationService = inject(ICmsNotificationService);
    private iCmsShortcutsService = inject(ICmsShortcutsService);

    /**
     * Resources we allow to search in
     */
    private readonly searchableResources = {
        product: {
            fields: ['name', 'sku', 'url'],
            title: 'Products'
        },
        category: {
            fields: ['name', 'url'],
            title: 'Categories'
        },
        page: {
            fields: ['name', 'url'],
            title: 'Pages'
        }
    };

    ngOnChanges(changes: SimpleChanges) {
        if (changes['query']) {
            this.status.busy = true;
            this.onTermChange();
        }
    }

    apply(item: TItem | undefined = this.selected) {
        if (item) {
            if ((item as NDfModal.IConfig).component && !(item as Model)?.data) {
                const modal = item as NDfModal.IConfig;
                try {
                    this.iCmsModalService.open(
                        undefined,
                        modal.id,
                        undefined,
                        undefined,
                        (modalRef: ModalRef<NICmsContentComponent.IComponent>) => {
                            const entry = modalRef.componentInstance;
                            this.timeoutService.setTimeout(() => {
                                if (entry._icmsContent$) {
                                    this.app.iCmsAdminEditorIntegrationICmsComponentService.editContent(entry._icmsContent$!.value!);
                                }
                            }, 500);
                        }
                    );
                } catch (err) {
                    this.iCmsNotificationService.add({
                        title: `Failed to open ${modal.name}`,
                        type: 'error',
                        copy: this.iCmsNotificationService.toErrorMsg(err),
                        duration: 0
                    });
                }
            } else {
                const url = (item as Model<IModelDataV2WithUrl>).data!.url;
                if (url) {
                    this.router.navigateByUrl(url);
                }
            }
            this.iCmsShortcutsService.destroyComponent();
        }
    }

    shiftSelection(shift: 1 | -1) {
        if (this.state !== 'results') {
            return;
        }
        const selected = this.selected || this.results?.[0]?.items[0];
        if (selected) {
            if (!this.selected) {
                this.selected = selected;
            } else {
                const groupIndex = this.results!.findIndex(result => result.items.includes(selected as any));
                const index = this.results![groupIndex].items.indexOf(selected as any);

                const shiftedItem = this.results![groupIndex].items[index + shift];
                if (shiftedItem) {
                    this.selected = shiftedItem;
                } else {
                    const nextGroup =
                        this.results![groupIndex + shift] || (shift > 0 ? this.results![0] : this.results![this.results!.length - 1]);
                    if (nextGroup) {
                        this.selected = nextGroup.items[shift > 0 ? 0 : nextGroup.items.length - 1];
                    }
                }
                this.detectChanges();
            }
        }
    }

    protected isSelected(item: TItem) {
        return this.selected === item;
    }

    protected select(item: TItem) {
        if (this.selected !== item) {
            this.selected = item;
            this.detectChanges();
        }
    }

    protected get state(): 'empty' | 'busy' | 'no-results' | 'results' {
        if (!this.hasValidQuery) {
            return 'empty';
        }
        if (!isEmpty(this.results)) {
            return 'results';
        }
        if (this.status.busy) {
            return 'busy';
        }
        return 'no-results';
    }

    private onTermChange = this.timeoutService.debounce(async () => {
        const query = this.query;
        const searchQuery = query.substring(1).trim(); // strip leading slash
        const result: TResult[] = [];
        if (this.hasValidQuery) {
            const matcher = { $regex: searchQuery, $options: 'i' };

            const promises = map(this.searchableResources, async (config, resourceName) => {
                const resource = this.app.resources[resourceName];
                if (!resource) {
                    return;
                }
                const filter: any = {
                    $or: config.fields.map(name => ({ [name]: matcher }))
                };
                const items = (await resource.find({
                    filter,
                    limit: 5,
                    sort: { created_at: -1 },
                    verbosity: 1
                })) as Model<IModelDataV2WithUrl>[];
                if (items.length) {
                    result.push({
                        title: config.title,
                        items
                    });
                }
                return;
            });

            await Promise.all(promises);

            const modals = this.iCmsModalService
                .getICmsModals()
                .filter(i => i.name?.toLowerCase().includes(searchQuery) || i.description?.toLowerCase().includes(searchQuery));
            if (modals.length) {
                result.push({
                    title: 'Pop-ups',
                    items: modals
                });
            }
        }

        if (this.query === query) {
            this.results = result;
            this.selected = result[0]?.items[0];
            this.status.busy = false;
            this.detectChanges();
        }
    }, 300);

    protected getItemInfo(item: TItem) {
        if ((item as Model).data) {
            if (item.type === Product.type) {
                return ` - id: ${item.id}, sku: ${((item as Model).data as IProductDataV1).sku}`;
            }
            return ` - id: ${item.id}, url: ${(item as Model<IModelDataV2WithUrl>).data!.url}`;
        }
        return '';
    }

    private detectChanges() {
        if (!(<ViewRef>this.changeDetectorRef).destroyed) {
            this.changeDetectorRef.detectChanges();
        }
    }

    private get hasValidQuery(): boolean {
        return this.query.substring(1).trim().length > 2;
    }
}
